'use client';

import { useSolanaPayment } from '@/components/profile/useSolanaPayment';
import { SpinnerInline } from '@/components/Spinner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useCreditBalance } from '@/hooks/useCreditBalance';
import { useGrantCreditsSecure } from '@/hooks/useGrantCreditsSecure';
import { useUser } from '@/hooks/useUser';
import type { CreditBundle } from '@/types/pricing';
import type { PaymentToken } from '@/types/solana';
import { usePrivy, useSolanaWallets } from '@privy-io/react-auth';
import * as Dialog from '@radix-ui/react-dialog';
import * as Select from '@radix-ui/react-select';
import { useGateValue } from '@statsig/react-bindings';
import { Check as CheckIcon, ChevronDown, Loader2, X } from 'lucide-react';
import { useRef, useState } from 'react';

const PLAN_A_BUNDLES: CreditBundle[] = [
  { name: 'shrimp', credits: 50, priceUSD: 15, priceSOL: 0.09, priceANTI: 10000, discount: 0 },
  { name: 'fish', credits: 300, priceUSD: 75, priceSOL: 0.45, priceANTI: 50000, discount: 16 },
  { name: 'dolphin', credits: 450, priceUSD: 90, priceSOL: 0.53, priceANTI: 57000, discount: 33 },
  { name: 'whale', credits: 1000, priceUSD: 150, priceSOL: 0.9, priceANTI: 100000, discount: 50 },
];
const PLAN_B_BUNDLES: CreditBundle[] = [
  { name: 'shrimp', credits: 40, priceUSD: 10, priceSOL: 0.06, priceANTI: 10000, discount: 0 },
  { name: 'fish', credits: 240, priceUSD: 20, priceSOL: 0.24, priceANTI: 50000, discount: 16 },
  { name: 'dolphin', credits: 400, priceUSD: 40, priceSOL: 0.32, priceANTI: 57000, discount: 33 },
  { name: 'whale', credits: 800, priceUSD: 75, priceSOL: 0.45, priceANTI: 100000, discount: 50 },
];

function getCreditBundles(): CreditBundle[] {
  const isPlanB = useGateValue('plan-bundle-b');
  return isPlanB ? PLAN_B_BUNDLES : PLAN_A_BUNDLES;
}

const PAYMENT_OPTIONS: { value: PaymentToken; label: string }[] = [
  { value: 'SOL', label: 'SOL' },
  { value: 'ANTIRUG', label: '$ANTIRUG' },
  { value: 'USDC', label: 'USDC' },
];

function getBundleLabel(opt: CreditBundle, paymentMethod: PaymentToken) {
  let price = '';
  if (paymentMethod === 'SOL') price = `${opt.priceSOL} SOL`;
  else if (paymentMethod === 'USDC') price = `$${opt.priceUSD}`;
  else if (paymentMethod === 'ANTIRUG') price = `${opt.priceANTI.toLocaleString()} $ANTIRUG`;
  return `${opt.credits} credits - ${price}${opt.discount ? ` (${opt.discount}% off)` : ''}`;
}

export function CreditsModal({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { user } = useUser();
  const userId = user?.user_id;
  const { data: balanceData, isLoading: isBalanceLoading, refetch } = useCreditBalance(userId);
  const {
    mutateAsync: grantCreditsSecure,
    isPending: isGrantingSecure,
    error: grantSecureError,
    isSuccess: grantSecureSuccess,
    reset: resetGrantSecure,
  } = useGrantCreditsSecure();
  const { pay, status: paymentStatus, error: paymentError } = useSolanaPayment();
  const bundles = getCreditBundles();
  const [selectedBundle, setSelectedBundle] = useState<CreditBundle>(bundles[0]);
  const [paymentMethod, setPaymentMethod] = useState<PaymentToken>('SOL');
  const [submitting, setSubmitting] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const { connectWallet, authenticated } = usePrivy();
  const { wallets: solanaWallets } = useSolanaWallets();
  const walletsRef = useRef(solanaWallets);

  async function handlePurchase() {
    if (!userId) return;
    setConnectionError(null);
    setSubmitting(true);
    try {
      if (!solanaWallets || solanaWallets.length === 0) {
        setConnectionError('Wallet connection required to purchase credits.');
        setSubmitting(false);
        return;
      }
      const sig = await pay(selectedBundle.priceSOL, paymentMethod);
      if (!sig) {
        setSubmitting(false);
        return;
      }
      await grantCreditsSecure({
        user_id: userId,
        amount: selectedBundle.credits,
        reason: 'purchase',
        purchased_with: paymentMethod,
        tx_signature: sig,
      });
      await refetch();
    } finally {
      setSubmitting(false);
    }
  }

  function handleClose() {
    resetGrantSecure();
    onOpenChange(false);
  }

  return (
    <Dialog.Root open={open} onOpenChange={handleClose} modal>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/60 z-40" />
        <Dialog.Content
          className="fixed z-50 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md p-0 border-none bg-transparent shadow-none"
          onInteractOutside={(e: Event) => e.preventDefault()}
          onEscapeKeyDown={(e: KeyboardEvent) => e.preventDefault()}
        >
          <Card className="w-full max-w-md">
            <CardHeader className="flex flex-row items-center justify-between border-b border-gray-800 p-4 pb-2">
              <CardTitle className="text-lg text-purple-400">Credits</CardTitle>
              <Dialog.Close asChild>
                <button className="rounded-full p-1 hover:bg-gray-800 text-gray-400 hover:text-white transition-colors">
                  <X size={20} />
                </button>
              </Dialog.Close>
            </CardHeader>
            <CardContent className="flex flex-col gap-6 p-6">
              <div className="flex flex-col items-center gap-2">
                <span className="text-purple-400 text-sm">Current Balance</span>
                <div className="text-3xl font-bold text-white">
                  {isBalanceLoading ? (
                    <Loader2 className="w-6 h-6 animate-spin" />
                  ) : (
                    (balanceData?.balance ?? 0)
                  )}
                </div>
              </div>
              {!solanaWallets || solanaWallets.length === 0 ? (
                <button
                  type="button"
                  className="h-10 w-fit px-4 py-2 flex flex-row gap-2 items-center justify-center rounded-md relative whitespace-nowrap transition-all duration-500 bg-gradient-to-r from-blue-500/30 to-purple-500/30 bg-size-200 bg-pos-0 hover:bg-pos-100 disabled:opacity-60 mt-4"
                  onClick={async () => {
                    setConnectionError(null);
                    setSubmitting(true);
                    try {
                      await connectWallet();
                    } finally {
                      setSubmitting(false);
                    }
                  }}
                  disabled={submitting}
                >
                  <span className="flex items-center justify-center min-w-[80px] h-full bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent font-semibold">
                    {submitting ? <SpinnerInline className="w-6 h-6" /> : 'Connect Wallet'}
                  </span>
                </button>
              ) : (
                <form
                  className="flex flex-col gap-4"
                  onSubmit={e => {
                    e.preventDefault();
                    handlePurchase();
                  }}
                >
                  <div>
                    <label className="block text-purple-400 mb-1 text-sm">
                      Select Credit Package
                    </label>
                    <Select.Root
                      value={selectedBundle.name}
                      onValueChange={name => {
                        const bundle = bundles.find(b => b.name === name);
                        if (bundle) setSelectedBundle(bundle);
                      }}
                      disabled={isGrantingSecure || submitting}
                    >
                      <Select.Trigger className="w-full flex items-center justify-between rounded-lg border border-gray-700 bg-gray-800 text-white px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-400">
                        <Select.Value />
                        <Select.Icon>
                          <ChevronDown className="w-4 h-4 text-purple-400" />
                        </Select.Icon>
                      </Select.Trigger>
                      <Select.Portal>
                        <Select.Content className="z-50 rounded-lg bg-gray-900 border border-gray-700 shadow-lg mt-1">
                          <Select.Viewport className="p-1">
                            {bundles.map(opt => (
                              <Select.Item
                                key={opt.name}
                                value={opt.name}
                                className="flex items-center px-3 py-2 rounded-md text-white cursor-pointer select-none focus:bg-purple-900/40 focus:text-purple-400 data-[state=checked]:bg-purple-900/40 data-[state=checked]:text-purple-400"
                              >
                                <Select.ItemText>
                                  {getBundleLabel(opt, paymentMethod)}
                                </Select.ItemText>
                                <Select.ItemIndicator className="ml-auto">
                                  <CheckIcon className="w-4 h-4 text-purple-400" />
                                </Select.ItemIndicator>
                              </Select.Item>
                            ))}
                          </Select.Viewport>
                        </Select.Content>
                      </Select.Portal>
                    </Select.Root>
                  </div>
                  <div>
                    <label className="block text-purple-400 mb-1 text-sm">Payment Method</label>
                    <Select.Root
                      value={paymentMethod}
                      onValueChange={(v: PaymentToken) => setPaymentMethod(v)}
                      disabled={isGrantingSecure || submitting}
                    >
                      <Select.Trigger className="w-full flex items-center justify-between rounded-lg border border-gray-700 bg-gray-800 text-white px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-400">
                        <Select.Value />
                        <Select.Icon>
                          <ChevronDown className="w-4 h-4 text-purple-400" />
                        </Select.Icon>
                      </Select.Trigger>
                      <Select.Portal>
                        <Select.Content className="z-50 rounded-lg bg-gray-900 border border-gray-700 shadow-lg mt-1">
                          <Select.Viewport className="p-1">
                            {PAYMENT_OPTIONS.map(opt => (
                              <Select.Item
                                key={opt.value}
                                value={opt.value}
                                className="flex items-center px-3 py-2 rounded-md text-white cursor-pointer select-none focus:bg-purple-900/40 focus:text-purple-400 data-[state=checked]:bg-purple-900/40 data-[state=checked]:text-purple-400"
                              >
                                <Select.ItemText>{opt.label}</Select.ItemText>
                                <Select.ItemIndicator className="ml-auto">
                                  <CheckIcon className="w-4 h-4 text-purple-400" />
                                </Select.ItemIndicator>
                              </Select.Item>
                            ))}
                          </Select.Viewport>
                        </Select.Content>
                      </Select.Portal>
                    </Select.Root>
                  </div>
                  {grantSecureError && (
                    <div className="text-red-400 text-sm">
                      {(grantSecureError as Error).message}
                    </div>
                  )}
                  {grantSecureSuccess && (
                    <div className="text-green-400 text-sm">Credits purchased successfully!</div>
                  )}
                  {paymentError && (
                    <div className="text-red-400 text-sm">
                      Payment error: {paymentError.message}
                    </div>
                  )}
                  {connectionError && <div className="text-red-400 text-sm">{connectionError}</div>}
                  <button
                    type="submit"
                    className="h-10 w-fit px-4 py-2 flex flex-row gap-2 items-center justify-center rounded-md relative whitespace-nowrap transition-all duration-500 bg-gradient-to-r from-blue-500/30 to-purple-500/30 bg-size-200 bg-pos-0 hover:bg-pos-100 disabled:opacity-60"
                    disabled={
                      isGrantingSecure || submitting || !userId || paymentStatus === 'pending'
                    }
                  >
                    <span className="flex items-center justify-center min-w-[80px] h-full bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent font-semibold">
                      {isGrantingSecure || submitting || paymentStatus === 'pending' ? (
                        <SpinnerInline className="w-6 h-6" />
                      ) : (
                        'Purchase'
                      )}
                    </span>
                  </button>
                </form>
              )}
            </CardContent>
          </Card>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
